/*
 * This file is part of glowingblue/gamilytics.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import { slug } from '../../../common';
import LeaderboardSidebar from './LeaderboardSidebar';
import LeaderboardToolbar from './LeaderboardToolbar';
import LeaderboardItemList from './LeaderboardItemList';
import LeaderboardPodium from './LeaderboardPodium';
import LeaderboardState from '../states/LeaderboardState';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Button from 'flarum/common/components/Button';
import Placeholder from 'flarum/common/components/Placeholder';
import LeaderboardHero from './LeaderboardHero';
import ItemList from 'flarum/common/utils/ItemList';

// Make translation calls shorter
const t = (...args) => app.translator.trans(...args);
const prfx = `${slug}.forum.leaderboard`;

export default class LeaderboardPage extends Page {
	oninit(vnode) {
		super.oninit(vnode);

		const gamificationEnabled = app.forum.attribute(`${slug}.gamification.enabled`);
		if (!gamificationEnabled) {
			m.route.set('/');
		}

		app.history.push('leaderboard');
		this.bodyClass = 'App--leaderboard';
		app.setTitle(t(`${prfx}.title`));

		if (!app.leaderboard) {
			app.leaderboard = new LeaderboardState();
		}

		const params = {
			activityType: m.route.param('activityType') || 'exp',
			timeSpan: m.route.param('timeSpan') || 'month',
		};

		const groupFilter = m.route.param('group');
		if (groupFilter) {
			params.group = groupFilter;
		}

		const levelFilter = m.route.param('level');
		if (levelFilter) {
			params.level = levelFilter;
		}

		app.leaderboard.refreshParams(params);

		app.leaderboard.refreshActivities();
	}

	view() {
		return (
			<div className="LeaderboardPage">
				{this.heroViewItems().toArray()}
				<div className="container">
					<div className="sideNavContainer">
						<nav className="LeaderboardPage-nav sideNav">{this.navViewItems().toArray()}</nav>
						<div className="LeaderboardPage-content sideNavOffset">{this.contentViewItems().toArray()}</div>
					</div>
				</div>
			</div>
		);
	}

	heroViewItems() {
		const items = new ItemList();

		items.add('leaderboard-hero', <LeaderboardHero />, 50);

		return items;
	}

	heroView() {
		return <LeaderboardHero />;
	}

	navViewItems() {
		const items = new ItemList();

		items.add('sidebar', this.sidebarView(), 80);

		return items;
	}

	sidebarView() {
		return <LeaderboardSidebar />;
	}

	contentViewItems() {
		const items = new ItemList();

		items.add('toolbar', this.toolbarView(), 80);
		items.add('podium', this.podiumView(), 60);
		items.add('itemList', this.itemListView(), 40);
		items.add('loadMore', this.loadMoreView(), 20);

		return items;
	}

	toolbarView() {
		return <LeaderboardToolbar />;
	}

	podiumView() {
		return <LeaderboardPodium />;
	}

	itemListView() {
		return <LeaderboardItemList />;
	}

	loadMoreView() {
		const state = app.leaderboard;

		let loading;

		if (state.isInitialLoading() || state.isLoadingNext()) {
			loading = <LoadingIndicator />;
		} else if (state.hasNext()) {
			loading = (
				<Button className="Button Button--primary" onclick={state.loadNext.bind(state)}>
					{t(`${prfx}.load_more_button`)}
				</Button>
			);
		}

		if (state.isEmpty()) {
			loading = <Placeholder text={t(`${prfx}.empty_text`)} />;
		}

		return <div className="LeaderboardPage-loadMore">{loading}</div>;
	}
}
